/** Keep diagnostic structure while removing credentials from every telemetry channel. */
const PRIVATE_KEY =
  /password|passwd|secret|token|ticket|cookie|authorization|api.?key|private.?key|signature|credential|csrf|requestBody|responseBody/i;

/** `name: value` / `name=value` pairs whose value is a credential, built from named parts for readability. */
const SECRET_NAME = 'password|passwd|secret|token|ticket|authorization|cookie|api[_-]?key';
const SECRET_VALUE = String.raw`"[^"]*"|'[^']*'|[^\s,;&]+`;
const SECRET_ASSIGNMENT = new RegExp(String.raw`((?:${SECRET_NAME})["']*\s*[:=]\s*)(?:${SECRET_VALUE})`, 'gi');

export function telemetryUrl(value: string): string {
  try {
    const url = new URL(value, 'https://relative.invalid');
    const path = url.pathname.replace(
      /(\/(?:reset-password|password\/reset|impersonate|console)\/)[^/]+/gi,
      '$1[Filtered]'
    );
    return (url.hostname === 'relative.invalid' ? '' : url.origin) + path;
  } catch {
    return '[Filtered URL]';
  }
}

export function telemetryText(value: string): string {
  return value
    .replace(/https?:\/\/[^\s<>"']+/gi, (url) => telemetryUrl(url))
    .replace(/\b(Bearer|Basic)\s+[\w.~+/=-]+/gi, '$1 [Filtered]')
    .replace(SECRET_ASSIGNMENT, '$1[Filtered]')
    .replace(/-----BEGIN [^-]*PRIVATE KEY-----[\s\S]*?-----END [^-]*PRIVATE KEY-----/g, '[Filtered private key]')
    .slice(0, 8192);
}

export function sanitizeTelemetry<T>(value: T): T {
  const seen = new WeakSet<object>();
  const visit = (item: unknown, key = '', depth = 0): unknown => {
    if (
      PRIVATE_KEY.test(key) ||
      ['cookies', 'headers', 'query_string', 'request_body', 'response_body'].includes(key)
    ) {
      return '[Filtered]';
    }
    if (depth > 12) return '[Truncated]';
    if (typeof item === 'string') {
      if (/^(url|href|from|to|http.url|url.full)$/i.test(key)) return telemetryUrl(item);
      return telemetryText(item);
    }
    if (item === null || typeof item !== 'object') return item;
    if (seen.has(item)) return '[Circular]';
    seen.add(item);
    const result = Array.isArray(item)
      ? item.map((child) => visit(child, '', depth + 1))
      : Object.fromEntries(Object.entries(item).map(([name, child]) => [name, visit(child, name, depth + 1)]));
    seen.delete(item);
    return result;
  };
  return visit(value) as T;
}
